import { computePosition, offset, flip, shift } from 'https://cdn.jsdelivr.net/npm/@floating-ui/dom@1.7.1/+esm';

document.addEventListener('DOMContentLoaded', function() {
  const heroBannerElement = document.getElementById("hotspot-hero__banner");
  if (!heroBannerElement) {
    return;
  }

  let globalActivePopup = null;
  const sectionId = "hotspot-hero__banner";

  function repositionHotspots() {
    const bannerW = heroBannerElement.offsetWidth;
    const bannerH = heroBannerElement.offsetHeight;
    if (!bannerW || !bannerH) return;

    const isMobile = window.innerWidth < 768;

    // Image intrinsic dimensions stored as data attributes on the section element
    const imgW = parseFloat(isMobile ? heroBannerElement.dataset.mobileImgW : heroBannerElement.dataset.desktopImgW) || 0;
    const imgH = parseFloat(isMobile ? heroBannerElement.dataset.mobileImgH : heroBannerElement.dataset.desktopImgH) || 0;

    // Focal point from the CSS custom property set in the inline <style> block (e.g. "35% 60%")
    const focalStr = window.getComputedStyle(heroBannerElement)
      .getPropertyValue(isMobile ? '--hero-banner-focal-mobile' : '--hero-banner-focal-desktop')
      .trim();

    let fx = 0.5, fy = 0.5;
    if (focalStr) {
      const parts = focalStr.split(' ');
      fx = parseFloat(parts[0]) / 100 || 0.5;
      fy = parseFloat(parts[1]) / 100 || 0.5;
    }

    heroBannerElement.querySelectorAll('.hero-banner__hotspot').forEach(hotspot => {
      // pos values are stored as % of the image's actual pixel dimensions (0–100)
      const rawX = hotspot.style.getPropertyValue(isMobile ? '--hotspot-pos-x-mobile' : '--hotspot-pos-x-desktop');
      const rawY = hotspot.style.getPropertyValue(isMobile ? '--hotspot-pos-y-mobile' : '--hotspot-pos-y-desktop');
      const imgFracX = parseFloat(rawX) / 100;
      const imgFracY = parseFloat(rawY) / 100;

      let x, y;

      if (imgW > 0 && imgH > 0) {
        // Replicate object-fit: cover geometry to find where the image pixel sits in the container
        const scale = Math.max(bannerW / imgW, bannerH / imgH);
        const renderedW = imgW * scale;
        const renderedH = imgH * scale;
        // Focal point anchors the rendered image within the container (same as object-position)
        const offsetX = (bannerW - renderedW) * fx;
        const offsetY = (bannerH - renderedH) * fy;

        // Map image-relative fraction → container percentage
        x = ((offsetX + imgFracX * renderedW) / bannerW) * 100;
        y = ((offsetY + imgFracY * renderedH) / bannerH) * 100;
      } else {
        // No image dimensions available — treat stored values as container-relative
        x = imgFracX * 100;
        y = imgFracY * 100;
      }

      hotspot.style.left = `${x}%`;
      hotspot.style.top = `${y}%`;
    });
  }

  // Use rAF so the container has its final computed dimensions before we position
  requestAnimationFrame(repositionHotspots);

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(repositionHotspots, 100);
  });

  function positionPopup(trigger, popup) {
    const baseAlignmentClass = Array.from(popup.classList).find(cls => cls.startsWith('hero-banner__hotspot-popup--align-'));
    let preferredPlacement = 'bottom';
    if (baseAlignmentClass) {
      preferredPlacement = baseAlignmentClass.replace('hero-banner__hotspot-popup--align-', '');
    }

    computePosition(trigger, popup, {
      placement: preferredPlacement,
      middleware: [
        offset(12),
        flip(),
        shift({ padding: 16 })
      ]
    }).then(({ x, y }) => {
      Object.assign(popup.style, {
        left: `${x}px`,
        top: `${y}px`,
        transform: 'none'
      });
    });
  }

  function closeGlobalActivePopup() {
    if (globalActivePopup) {
      const activeTrigger = heroBannerElement.querySelector(`.hero-banner__hotspot-trigger[aria-expanded="true"][aria-controls="${globalActivePopup.id}"]`);
      globalActivePopup.classList.remove('hero-banner__hotspot-popup--active');
      globalActivePopup.setAttribute('aria-hidden', 'true');
      if (activeTrigger) {
        activeTrigger.setAttribute('aria-expanded', 'false');
      }
      globalActivePopup = null;
    }
  }

  const hotspotContainer = heroBannerElement.querySelector('.hero-banner__hotspot-container');
  if (hotspotContainer && hotspotContainer.children.length > 0) {
    document.addEventListener('click', function(event) {
      const clickedCloseButton = event.target.closest(`#${sectionId} .hero-banner__hotspot-popup-close`);
      const clickedTrigger = event.target.closest(`#${sectionId} .hero-banner__hotspot-trigger`);

      if (clickedCloseButton && globalActivePopup && globalActivePopup.contains(clickedCloseButton)) {
        event.stopPropagation();
        closeGlobalActivePopup();
        return;
      }

      if (clickedTrigger && heroBannerElement.contains(clickedTrigger)) {
        event.stopPropagation();
        const popupId = clickedTrigger.getAttribute('aria-controls');
        const popup = document.getElementById(popupId);
        if (!popup) return;

        if (popup.classList.contains('hero-banner__hotspot-popup--active')) {
          closeGlobalActivePopup();
        } else {
          closeGlobalActivePopup();
          popup.classList.add('hero-banner__hotspot-popup--active');
          popup.setAttribute('aria-hidden', 'false');
          clickedTrigger.setAttribute('aria-expanded', 'true');
          globalActivePopup = popup;
          positionPopup(clickedTrigger, popup);
        }
      } else if (globalActivePopup && !globalActivePopup.contains(event.target) && !event.target.closest(`#${sectionId} .hero-banner__hotspot-trigger`)) {
        if (heroBannerElement.contains(event.target) || !document.body.contains(event.target)) {
        } else {
          closeGlobalActivePopup();
        }
      }
    });

    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' && globalActivePopup) {
        const triggerForActivePopup = heroBannerElement.querySelector(`.hero-banner__hotspot-trigger[aria-expanded="true"][aria-controls="${globalActivePopup.id}"]`);
        closeGlobalActivePopup();
        if (triggerForActivePopup) {
          triggerForActivePopup.focus();
        }
      }
    });
  }

  const textContentElement = heroBannerElement.querySelector('.hero-banner__text-content');
  if (textContentElement) {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('hero-banner__text-content--in-view');
          observer.unobserve(entry.target);
        }
      });
    };
    const intersectionObserver = new IntersectionObserver(observerCallback, observerOptions);
    intersectionObserver.observe(textContentElement);
  }
});
